import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Flex, Icon, Text } from "orcs-design-system";
import { isAllowedHelpSourceUrl } from "./consts";

const SourcesWrap = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${themeGet("colors.surfaceBorder")};
`;

const SourcesLinksRow = styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const SourcesLabel = styled(Text)`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${themeGet("colors.greyDarker")};
  opacity: 0.65;
  width: 100%;
  line-height: 1;
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: ${themeGet("colors.bodyText")};
  text-decoration: none;
  background: ${themeGet("colors.surfaceRaised")};
  border: 1px solid ${themeGet("colors.surfaceBorder")};
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.15s ease;

  &:hover {
    border-color: rgba(0, 145, 234, 0.3);
    background: ${themeGet("colors.hoverSurface")};
    box-shadow: 0 2px 8px rgba(0, 145, 234, 0.1);
    transform: translateY(-1px);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid rgba(0, 145, 234, 0.35);
    outline-offset: 2px;
  }
`;

const SourceTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * @param {unknown} citations
 * @returns {{ title: string, url: string, pageId?: string }[]}
 */
export const getHelpSourcesFromCitations = (citations) => {
  if (!citations || typeof citations !== "object" || Array.isArray(citations)) {
    return [];
  }
  const raw = /** @type {{ helpSources?: unknown }} */ (citations).helpSources;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const source = /** @type {{ title?: string, url?: string }} */ (item);
      return {
        title: source.title || "TeamForm Help",
        url: source.url || "",
        pageId: source.pageId,
      };
    })
    .filter((item) => isAllowedHelpSourceUrl(item.url));
};

const HelpSources = ({ citations }) => {
  const sources = getHelpSourcesFromCitations(citations);
  if (sources.length === 0) {
    return null;
  }

  return (
    <SourcesWrap role="navigation" aria-label="Help article sources">
      <SourcesLabel as="span">Sources</SourcesLabel>
      <SourcesLinksRow>
        {sources.map((source) => (
          <SourceLink
            key={source.pageId || source.url}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${source.title} in TeamForm Help`}
          >
            <Icon icon={["far", "file"]} fontSize="11px" aria-hidden />
            <SourceTitle>{source.title}</SourceTitle>
            <Icon icon={["far", "external-link"]} fontSize="10px" aria-hidden />
          </SourceLink>
        ))}
      </SourcesLinksRow>
    </SourcesWrap>
  );
};

HelpSources.propTypes = {
  citations: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default HelpSources;
